import React, { useMemo, useState } from "react";
import { useVenue } from "../VenueProvider";
import { useAuth } from "../AuthProvider";
import { showAlert } from "../../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";
import {
  crearCliente,
  limpiaDatosCliente,
  nombreCliente,
  contactoCliente,
  coincidenciasDeContacto,
  contactosDeCliente,
  normalizaCorreo,
  normalizaTelefono,
} from "../../utils/clientes";
import { ClienteFormFields, FORM_CLIENTE_VACIO, AvisoDuplicados } from "./ClienteCreateDialog";
import { parecidosAlAlta } from "../../utils/clientesBackfill";
import { Badge } from "../badge";
import {
  XMarkIcon,
  UserIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon,
  SparklesIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const normaliza = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

// Ligar el evento a una ficha de cliente, o crear la ficha.
//
// Antes esto vivía desparramado en la ficha del evento: un botón de texto que
// abría un buscador en línea, con otro botón de texto debajo que creaba la
// ficha EN SILENCIO con lo que hubiera en el evento. Tres decisiones
// distintas compitiendo por el mismo renglón, y la más destructiva —crear una
// ficha coja— era la de un clic.
//
// Aquí es un solo lugar con dos pasos claros: buscar, o crear. Las fichas
// que coinciden con el correo o el teléfono del evento se listan TODAS, al
// pie de la búsqueda: si hay dos con el mismo correo (un duplicado), quien
// liga las ve juntas y elige, en vez de recibir una al azar. Ligar a la que
// existe es casi siempre lo correcto y es lo que evita el directorio lleno
// de duplicados.
//
// El mismo diálogo sirve para CAMBIAR la ficha principal y para AGREGAR
// otra ficha al evento (el novio y la novia, la productora y quien paga):
// `excluir` esconde las que ya están en el evento, y `titulo`/`subtitulo`/
// `textoCrear` ajustan el texto al caso.
export default function ClienteEventoDialog({
  open,
  datosDelEvento,
  onClose,
  onLigar,
  excluir = [],
  titulo = "Cliente del evento",
  subtitulo = "Búscalo en el directorio o dalo de alta",
  textoCrear = "Crear y ligar al evento",
  // `origen` de la ficha cuando se crea desde aquí: evento | lead.
  origenCreacion = "evento",
  // Encima de otro diálogo (el de evento nuevo) necesita quedar arriba.
  zIndex = 50,
  // Todo el diálogo es escritura (ligar o crear ficha): en solo lectura
  // (inversionista, P-FRA-13) no se abre aunque alguien lo pida.
  soloLectura = false,
}) {
  const { selectedVenueId, clientes } = useVenue();
  const { userInfo, user } = useAuth();
  const [paso, setPaso] = useState("buscar"); // buscar | crear
  const [busqueda, setBusqueda] = useState("");
  const [form, setForm] = useState({ ...FORM_CLIENTE_VACIO, ...(datosDelEvento || {}) });
  const [saving, setSaving] = useState(false);

  // Abierto desde un evento o un lead con datos, el formulario de alta llega
  // prellenado; desde "Agregar otro cliente" llega vacío y no hay que
  // prometer lo contrario.
  const prellenado = !!(
    datosDelEvento?.nombre ||
    datosDelEvento?.nombreEmpresa ||
    datosDelEvento?.correo ||
    datosDelEvento?.telefono
  );

  const activos = useMemo(() => {
    const fuera = new Set((excluir || []).filter(Boolean));
    return (clientes || []).filter((c) => c && !c.archivedAt && !fuera.has(c.id));
  }, [clientes, excluir]);

  // Las coincidencias por correo/teléfono del evento, todas.
  const coincidencias = useMemo(
    () =>
      coincidenciasDeContacto(activos, {
        correo: datosDelEvento?.correo,
        telefono: datosDelEvento?.telefono,
      }),
    [activos, datosDelEvento]
  );

  const resultados = useMemo(() => {
    const q = normaliza(busqueda).trim();
    const coinciden = new Set(coincidencias.map((c) => c.id));
    const base = activos.filter((c) => !coinciden.has(c.id));
    if (!q) return base.slice(0, 8);
    const tel = normalizaTelefono(busqueda);
    return base
      .filter((c) => {
        if (normaliza(nombreCliente(c)).includes(q)) return true;
        if (normaliza(c.nombreEmpresa).includes(q)) return true;
        return contactosDeCliente(c).some(
          (x) =>
            normaliza(x.nombre).includes(q) ||
            normaliza(x.apellido).includes(q) ||
            normalizaCorreo(x.correo).includes(q) ||
            (tel.length >= 6 && normalizaTelefono(x.telefono).includes(tel))
        );
      })
      .slice(0, 12);
  }, [activos, busqueda, coincidencias]);

  const cerrar = () => {
    setPaso("buscar");
    setBusqueda("");
    setForm({ ...FORM_CLIENTE_VACIO, ...(datosDelEvento || {}) });
    onClose?.();
  };

  const crear = async () => {
    setSaving(true);
    try {
      const id = await crearCliente({
        venueId: selectedVenueId,
        datos: form,
        user,
        origen: origenCreacion,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.CLIENTE_CREATE,
          entity: "cliente",
          entityId: id,
          entityName: nombreCliente(form),
          details: { origen: origenCreacion },
        },
        userInfo,
        user
      );
      cerrar();
      // La ficha recién creada todavía no está en el snapshot del
      // VenueProvider: se devuelve tal como se capturó para que el llamador
      // no tenga que esperarla.
      onLigar?.(id, { creada: true, cliente: { id, ...limpiaDatosCliente(form) } });
    } catch (err) {
      showAlert(err.message || "No se pudo crear el cliente", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!open || soloLectura) return null;

  const FichaRow = ({ cliente, destacada = false }) => {
    const contactos = contactosDeCliente(cliente);
    const principal = contactos.find((c) => c.principal) || contactos[0];
    return (
      <button
        type="button"
        onClick={() => onLigar?.(cliente.id, { creada: false, cliente })}
        className={`w-full rounded-lg border px-3 py-2.5 text-left transition ${
          destacada
            ? "border-transparent ring-1"
            : "border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800/60"
        }`}
        style={destacada ? { backgroundColor: ACCENT + "0d", ringColor: ACCENT } : undefined}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-zinc-900 dark:text-white">
            {nombreCliente(cliente)}
          </span>
          {cliente.clientType === "moral" && <Badge color="zinc">Empresa</Badge>}
          {contactos.length > 1 && (
            <span className="text-xs text-zinc-400">
              {contactos.length} contactos
            </span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-zinc-500 break-words">
          {[contactoCliente(cliente), principal?.correo, principal?.telefono]
            .filter(Boolean)
            .join(" · ") || "Sin datos de contacto"}
        </div>
      </button>
    );
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex }}>
      <div className="absolute inset-0 bg-black/30" onClick={cerrar} />
      <div className="relative w-full max-w-lg rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900 flex flex-col max-h-[90vh]">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/60">
          {paso === "crear" && (
            <button
              type="button"
              onClick={() => setPaso("buscar")}
              className="p-1 -ml-1 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-800"
              title="Volver a buscar"
            >
              <ArrowLeftIcon className="h-4 w-4" />
            </button>
          )}
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: ACCENT + "1a" }}
          >
            <UserIcon className="h-4 w-4" style={{ color: ACCENT }} />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
              {paso === "crear" ? "Nuevo cliente" : titulo}
            </h2>
            <div className="text-xs text-zinc-500 truncate">
              {paso === "crear"
                ? prellenado
                  ? "Con los datos del evento, revísalos antes de crearlo"
                  : "Captura sus datos"
                : subtitulo}
            </div>
          </div>
          <button
            type="button"
            onClick={cerrar}
            className="ml-auto p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {paso === "buscar" ? (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <div>
                <div className="relative mb-2">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <input
                    type="text"
                    autoFocus
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Buscar por nombre, empresa, correo o teléfono…"
                    className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 pl-9 pr-3 py-2 text-sm text-zinc-950 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]"
                  />
                </div>
                {resultados.length === 0 ? (
                  <p className="py-3 text-center text-sm text-zinc-400">
                    {busqueda
                      ? "Nadie coincide con esa búsqueda."
                      : coincidencias.length > 0
                        ? "No hay más clientes en el directorio."
                        : "Todavía no hay clientes en el directorio."}
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {resultados.map((c) => (
                      <FichaRow key={c.id} cliente={c} />
                    ))}
                  </div>
                )}
              </div>

              {/* Las coincidencias van al pie, todas: con dos fichas del
                  mismo correo hay que verlas juntas para notar el
                  duplicado y elegir la buena. */}
              {coincidencias.length > 0 && (
                <div>
                  <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-400">
                    <SparklesIcon className="h-3.5 w-3.5" style={{ color: ACCENT }} />
                    {coincidencias.length === 1
                      ? "Coincide con el correo o teléfono del evento"
                      : `${coincidencias.length} clientes coinciden con el correo o teléfono del evento`}
                  </div>
                  <div className="space-y-1.5">
                    {coincidencias.map((c) => (
                      <FichaRow key={c.id} cliente={c} destacada />
                    ))}
                  </div>
                  {coincidencias.length > 1 && (
                    <p className="mt-1 text-xs text-zinc-500">
                      Parecen la misma persona. Liga el que quieras conservar y archiva el otro desde el directorio.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-zinc-200 dark:border-zinc-700/60 px-5 py-4">
              <button
                type="button"
                onClick={() => setPaso("crear")}
                className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                style={{ backgroundColor: ACCENT }}
              >
                Nuevo cliente
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <ClienteFormFields form={form} setForm={setForm} autoFocus />
              {/* Lo que se escribe se compara contra el directorio: desde un
                  lead o "Agregar otro cliente" el formulario llega vacío, y
                  sin esto se creaba el duplicado sin aviso. */}
              <AvisoDuplicados
                duplicados={parecidosAlAlta(form, activos)}
                onUsar={(c) => onLigar?.(c.id, { creada: false, cliente: c })}
              />
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-zinc-200 dark:border-zinc-700/60 px-5 py-4">
              <button
                type="button"
                onClick={() => setPaso("buscar")}
                className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={crear}
                disabled={saving}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: ACCENT }}
              >
                {saving ? "Creando…" : textoCrear}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
